import { useCallback, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const query = (params = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  );
  return new URLSearchParams(cleaned).toString();
};

const useAnalysisReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const request = useCallback(
    async (method, url, data, successMessage, { silentLoading = false } = {}) => {
      if (!silentLoading) setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(method, url, data, navigate);
        if (response?.ok) {
          if (successMessage) message.success(successMessage);
          return response.data ?? true;
        }
        Utilities.showError(response, response?.data?.detail || response?.msg || "Request failed");
        return null;
      } catch (error) {
        console.error(error);
        message.error("Request failed");
        return null;
      } finally {
        if (!silentLoading) setLoading(false);
      }
    },
    [navigate],
  );

  const fetchReports = useCallback(
    async (params = {}) => {
      const qs = query(params);
      return listFrom(await request("GET", `${Settings.urls.academics.analysisReports}${qs ? `?${qs}` : ""}`, null));
    },
    [request],
  );

  const fetchReportDetail = useCallback(
    (id, { silentLoading = false } = {}) =>
      request("GET", Settings.urls.academics.analysisReportDetail.replace(":id", id), null, null, { silentLoading }),
    [request],
  );

  const submitAction = useCallback(
    (reportId, data) =>
      request(
        "POST",
        Settings.urls.academics.analysisReportActions.replace(":reportId", reportId),
        data,
        "Review action submitted",
      ),
    [request],
  );

  const updateFlagStatus = useCallback(
    (actionId, flagStatus) =>
      request(
        "PATCH",
        Settings.urls.academics.analysisFlagStatus.replace(":actionId", actionId),
        { flagStatus },
        "Flag status updated",
      ),
    [request],
  );

  const fetchOpenFlags = useCallback(
    async (params = {}) => {
      const qs = query(params);
      return listFrom(await request("GET", `${Settings.urls.academics.analysisOpenFlags}${qs ? `?${qs}` : ""}`, null));
    },
    [request],
  );

  const downloadReportExport = useCallback(
    async (reportId, exportFile = null) => {
      const response = await Utilities.fetchWithAuth(
        "GET",
        Settings.urls.academics.analysisReportDownload.replace(":id", reportId),
        null,
        navigate,
        undefined,
        { responseType: "blob" },
      );
      if (response?.ok && response.data) {
        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const filename = exportFile ? String(exportFile).split("/").pop() : `academic_analysis_${reportId}.xlsx`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1500);
        return { ok: true };
      }
      return {
        ok: false,
        status: response?.status,
        exportStatus: response?.data?.exportStatus,
        exportError: response?.data?.exportError,
        detail: response?.data?.detail || response?.msg || "Download failed",
      };
    },
    [navigate],
  );

  return {
    loading,
    fetchReports,
    fetchReportDetail,
    submitAction,
    updateFlagStatus,
    fetchOpenFlags,
    downloadReportExport,
  };
};

export default useAnalysisReports;
